/**
 * Company Admin Service
 * API calls for company admin panel
 */

import axios from "axios";

// Use backend URL directly for company admin (not the proxy)
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";

// Create axios instance for company admin
const companyApi = axios.create({
  baseURL: `${BACKEND_URL}/api/company`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
companyApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("companyAdminToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
companyApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("companyAdminToken");
      localStorage.removeItem("companyAdminUser");
      window.location.href = "/company/login";
    }
    return Promise.reject(error);
  }
);

/**
 * Authentication
 */
export const login = async (email, password) => {
  const response = await companyApi.post("/auth/login", { email, password });
  const { token, user, company } = response.data.data;

  localStorage.setItem("companyAdminToken", token);
  localStorage.setItem("companyAdminUser", JSON.stringify({ user, company }));

  return { user, company };
};

export const register = async ({ companyName, email, password, name, phone }) => {
  const response = await companyApi.post("/auth/register", {
    companyName,
    email,
    password,
    name,
    phone,
  });
  const { token, user, company } = response.data.data;

  localStorage.setItem("companyAdminToken", token);
  localStorage.setItem("companyAdminUser", JSON.stringify({ user, company }));

  return { user, company };
};

export const sendSmsLogin = async (phone) => {
  const response = await companyApi.post("/auth/sms/send", { phone });
  return response.data.data;
};

export const verifySmsLogin = async (phone, code) => {
  const response = await companyApi.post("/auth/sms/verify", { phone, code });
  const { token, user, company } = response.data.data;

  localStorage.setItem("companyAdminToken", token);
  localStorage.setItem("companyAdminUser", JSON.stringify({ user, company }));

  return { user, company };
};

export const logout = () => {
  localStorage.removeItem("companyAdminToken");
  localStorage.removeItem("companyAdminUser");
  localStorage.removeItem("company_sidebar_logo");
  localStorage.removeItem("company_sidebar_name");
};

export const getCurrentUser = () => {
  const data = localStorage.getItem("companyAdminUser");
  return data ? JSON.parse(data) : null;
};

export const isAuthenticated = () => {
  return !!localStorage.getItem("companyAdminToken");
};

/**
 * Profile
 */
export const getProfile = async () => {
  const response = await companyApi.get("/profile");
  return response.data.data;
};

export const updateProfile = async (updates) => {
  const response = await companyApi.put("/profile", updates);
  return response.data.data;
};

/**
 * Upload company logo (base64 data URL)
 */
export const uploadCompanyLogo = async (imageDataUrl) => {
  const response = await companyApi.post("/profile/logo", { image: imageDataUrl });
  return response.data.data;
};

/**
 * Statistics
 */
export const getStats = async (days = 30) => {
  const response = await companyApi.get(`/stats?days=${days}`);
  return response.data.data;
};

/**
 * Contracts
 */
export const getContracts = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append("status", params.status);
  if (params.search) queryParams.append("search", params.search);
  if (params.fromDate) queryParams.append("fromDate", params.fromDate);
  if (params.toDate) queryParams.append("toDate", params.toDate);
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.expiringWithinHours != null)
    queryParams.append("expiringWithinHours", params.expiringWithinHours);

  const response = await companyApi.get(`/contracts?${queryParams.toString()}`);
  return response.data.data;
};

export const getContract = async (contractId) => {
  const response = await companyApi.get(`/contracts/${contractId}`);
  return response.data.data;
};

export const getContractViewLink = async (contractId) => {
  const response = await companyApi.get(`/contracts/${contractId}/view-link`);
  return response.data.data.viewLink;
};

export const resendContract = async (contractId) => {
  const response = await companyApi.post(`/contracts/${contractId}/resend`);
  return response.data.data;
};

export const cancelContract = async (contractId, reason) => {
  const response = await companyApi.post(`/contracts/${contractId}/cancel`, {
    reason: reason || undefined,
  });
  return response.data.data;
};

export const createContract = async (payload) => {
  const response = await companyApi.post("/contracts", payload);
  return response.data.data;
};

export const deleteContract = async (contractId) => {
  const response = await companyApi.delete(`/contracts/${contractId}`);
  return response.data.data;
};

/**
 * API Keys
 */
export const getApiKeys = async () => {
  const response = await companyApi.get("/api-keys");
  return response.data.data;
};

export const rotateApiKeys = async (type = "production") => {
  const response = await companyApi.post("/api-keys/rotate", { type });
  return response.data.data;
};

/**
 * Webhooks
 */
export const getWebhookConfig = async () => {
  const response = await companyApi.get("/webhook");
  return response.data.data;
};

export const updateWebhookConfig = async (config) => {
  const response = await companyApi.put("/webhook", config);
  return response.data.data;
};

export const rotateWebhookSecret = async (type = "production") => {
  const response = await companyApi.post("/webhook/rotate-secret", { type });
  return response.data.data;
};

export const testWebhook = async (type = "production") => {
  const response = await companyApi.post("/webhook/test", { type });
  return response.data.data;
};

export const getWebhookLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append("status", params.status);
  if (params.eventType) queryParams.append("eventType", params.eventType);
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.fromDate) queryParams.append("fromDate", params.fromDate);
  if (params.toDate) queryParams.append("toDate", params.toDate);

  const response = await companyApi.get(
    `/webhook/logs?${queryParams.toString()}`
  );
  return response.data.data;
};

export const getWebhookStats = async (days = 30) => {
  const response = await companyApi.get(`/webhook/stats?days=${days}`);
  return response.data.data;
};

export const retryWebhook = async (webhookLogId) => {
  const response = await companyApi.post("/webhook/retry", { webhookLogId });
  return response.data.data;
};

export const deleteWebhookLogs = async (ids) => {
  const response = await companyApi.delete("/webhook/logs", { data: { ids } });
  return response.data.data;
};

/**
 * Document designs (for template design picker)
 */
export const getDocumentDesigns = async () => {
  const response = await companyApi.get("/document-designs");
  return response.data.data;
};

/**
 * Templates
 */
export const getTemplates = async () => {
  const response = await companyApi.get("/templates");
  return response.data.data;
};

export const getTemplate = async (templateId) => {
  const response = await companyApi.get(`/templates/${templateId}`);
  return response.data.data;
};

export const createTemplate = async (data) => {
  const response = await companyApi.post("/templates", data);
  return response.data.data;
};

export const updateTemplate = async (templateId, data) => {
  const response = await companyApi.put(`/templates/${templateId}`, data);
  return response.data.data;
};

/**
 * Generate template HTML using AI from structured inputs
 * @param {Object} params - documentType, language, direction, title, partiesDescription, requiredPlaceholders, optionalPlaceholders, customPlaceholders, contentDescription, requiredClauses, forbiddenClauses, tone, length, structure, includeLogo
 * @returns {Promise<{ templateHtml, suggestedName?, suggestedDescription?, placeholders, errors? }>}
 */
export const generateTemplate = async (params) => {
  const response = await companyApi.post("/templates/generate", params);
  return response.data.data;
};

export const deleteTemplate = async (templateId) => {
  const response = await companyApi.delete(`/templates/${templateId}`);
  return response.data.data;
};

export const previewTemplate = async (templateHtml, variables = {}) => {
  const response = await companyApi.post("/templates/preview", {
    templateHtml,
    variables,
  });
  return response.data.data;
};

/**
 * Merge document design + contentHtml for preview (design built in GUI)
 */
export const mergePreviewTemplate = async (documentDesignId, documentDesignColors = {}, contentHtml = "") => {
  const response = await companyApi.post("/templates/merge-preview", {
    documentDesignId,
    documentDesignColors,
    contentHtml,
  });
  return response.data.data;
};

/**
 * Clients
 */
export const getClients = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append("search", params.search);
  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
  if (params.hasEmail === true) queryParams.append("hasEmail", "true");
  if (params.hasEmail === false) queryParams.append("hasEmail", "false");
  if (params.createdAtFrom) queryParams.append("createdAtFrom", params.createdAtFrom);
  if (params.createdAtTo) queryParams.append("createdAtTo", params.createdAtTo);
  if (params.updatedAtFrom) queryParams.append("updatedAtFrom", params.updatedAtFrom);
  if (params.updatedAtTo) queryParams.append("updatedAtTo", params.updatedAtTo);
  const response = await companyApi.get(`/clients?${queryParams.toString()}`);
  return response.data.data;
};

export const getClient = async (clientId) => {
  const response = await companyApi.get(`/clients/${clientId}`);
  return response.data.data;
};

export const createClient = async (data) => {
  const response = await companyApi.post("/clients", data);
  return response.data.data;
};

export const updateClient = async (clientId, data) => {
  const response = await companyApi.put(`/clients/${clientId}`, data);
  return response.data.data;
};

export const deleteClient = async (clientId) => {
  const response = await companyApi.delete(`/clients/${clientId}`);
  return response.data.data;
};

export const importClients = async (clients) => {
  const response = await companyApi.post("/clients/import", { clients });
  return response.data.data;
};

/**
 * Team
 */
export const getTeam = async () => {
  const response = await companyApi.get("/team");
  return response.data.data;
};

export const addTeamMember = async (userData) => {
  const response = await companyApi.post("/team", userData);
  return response.data.data;
};

export const removeTeamMember = async (userId) => {
  const response = await companyApi.delete(`/team/${userId}`);
  return response.data.data;
};

/**
 * Security
 */
export const getSecuritySettings = async () => {
  const response = await companyApi.get("/security");
  return response.data.data;
};

export const updateSecuritySettings = async (settings) => {
  const response = await companyApi.put("/security", settings);
  return response.data.data;
};

export default {
  login,
  register,
  sendSmsLogin,
  verifySmsLogin,
  logout,
  getCurrentUser,
  isAuthenticated,
  getProfile,
  updateProfile,
  getStats,
  getContracts,
  getContract,
  getContractViewLink,
  cancelContract,
  createContract,
  deleteContract,
  getApiKeys,
  rotateApiKeys,
  getWebhookConfig,
  updateWebhookConfig,
  rotateWebhookSecret,
  testWebhook,
  getWebhookLogs,
  getWebhookStats,
  retryWebhook,
  deleteWebhookLogs,
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  previewTemplate,
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  getTeam,
  addTeamMember,
  removeTeamMember,
  getSecuritySettings,
  updateSecuritySettings,
};
